import { forwardRef, useMemo, useState } from "react";
import { Controller, type ControllerFieldState, type ControllerProps, type FieldValues } from "react-hook-form";

import { useFindFamilyContact, type FamilyContact } from "../../hooks/use-families";
import { ComboboxItem, SearchSelect } from "./search-select";
import { useDebounce } from "../../hooks/use-debounce";

interface ContactSearchProps {
  onSelect?: (contact: FamilyContact) => void;
  label?: string;
  addNewLabel?: string;
  selectedContact?: FamilyContact;
  onCreateNewClick?: () => void;
  onBlur?: () => void;
  fieldState?: ControllerFieldState;
  name: string;
  isRequired?: boolean;
  disabled?: boolean;
  contactsOverride?: FamilyContact[];
  isLoadingContactsOverride?: boolean;
  showCreateNewOption?: boolean;
  isSearchable?: boolean;
}

export const ContactSearch = forwardRef<HTMLDivElement, ContactSearchProps>(
  (
    {
      onSelect,
      label = "Contact",
      addNewLabel = "Create New Contact",
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onCreateNewClick = () => {},
      selectedContact,
      onBlur,
      fieldState,
      name,
      isRequired,
      disabled,
      contactsOverride,
      isLoadingContactsOverride,
      showCreateNewOption = true,
      isSearchable = true
    },
    ref
  ) => {
    const [searchValue, setSearchValue] = useState<string>("");
    const debouncedSearchInput = useDebounce(searchValue, 350);
    const shouldUseServerSearch = contactsOverride === undefined;
    const { contacts, isLoading } = useFindFamilyContact(debouncedSearchInput, shouldUseServerSearch);

    const handleSearchChange = (searchValue = "") => {
      setSearchValue(searchValue);
    };

    const handleSelectChange = (item?: ComboboxItem | null) => {
      //@ts-ignore
      onSelect && onSelect(item?.contact);
    };

    const filteredOverriddenContacts = useMemo(() => {
      if (!contactsOverride) {
        return [];
      }
      if (!isSearchable) {
        return contactsOverride;
      }

      const search = searchValue.trim().toLowerCase();
      if (!search.length) {
        return contactsOverride;
      }

      return contactsOverride.filter(contact => contact.displayName.toLowerCase().includes(search));
    }, [contactsOverride, searchValue, isSearchable]);

    const resolvedContacts = contactsOverride ?? contacts;

    const items = useMemo(
      () =>
        resolvedContacts.map(contact => ({
          value: contact.id,
          label: contact.displayName,
          contact
        })),
      [resolvedContacts]
    );

    const overriddenItems = useMemo(
      () =>
        filteredOverriddenContacts.map(contact => ({
          value: contact.id,
          label: contact.displayName,
          contact
        })),
      [filteredOverriddenContacts]
    );

    const selectedItem = selectedContact
      ? {
          label: selectedContact.displayName,
          value: selectedContact.id
        }
      : undefined;

    const options = contactsOverride ? overriddenItems : items;
    const isLoadingOptions = contactsOverride ? isLoadingContactsOverride ?? false : isLoading;

    const createNewOptionProps = showCreateNewOption ? { onCreateNewClick, label: addNewLabel } : undefined;

    return (
      <SearchSelect
        onSearchChange={handleSearchChange}
        createNewOptionProps={createNewOptionProps}
        selectedItem={selectedItem}
        options={options}
        onChange={handleSelectChange}
        toggleLabel={label}
        onBlur={onBlur}
        ref={ref}
        fieldState={fieldState}
        name={name}
        isLoadingOptions={isLoadingOptions}
        searchValue={searchValue}
        isRequired={isRequired}
        disabled={disabled}
        showSearch={isSearchable}
      />
    );
  }
);

ContactSearch.displayName = "ContactSearch";

type ContactSearchControllerProps<T extends FieldValues> = Omit<ControllerProps<T>, "render"> &
  Omit<ContactSearchProps, "selectedContact" | "onBlur" | "fieldState" | "name">;

export const ContactSearchController = <T extends FieldValues>({
  onSelect,
  label,
  addNewLabel = "Add New Contact",
  onCreateNewClick,
  isRequired,
  disabled,
  contactsOverride,
  isLoadingContactsOverride,
  showCreateNewOption,
  isSearchable,
  ...controllerProps
}: ContactSearchControllerProps<T>) => {
  return (
    <Controller
      {...controllerProps}
      render={({ field, fieldState }) => {
        const handleSelect = (value: FamilyContact) => {
          onSelect?.(value);
          field.onChange(value);
        };
        return (
          <ContactSearch
            addNewLabel={addNewLabel}
            onCreateNewClick={onCreateNewClick}
            label={label}
            selectedContact={field.value}
            onSelect={handleSelect}
            onBlur={field.onBlur}
            ref={field.ref}
            fieldState={fieldState}
            name={field.name}
            isRequired={isRequired}
            disabled={disabled}
            contactsOverride={contactsOverride}
            isLoadingContactsOverride={isLoadingContactsOverride}
            showCreateNewOption={showCreateNewOption}
            isSearchable={isSearchable}
          />
        );
      }}
    />
  );
};
